/**
 * SPEC.md §10 — M6.8 (issue #35): Cmd/Ctrl+K. A searchable index over every
 * action already reachable via a menu or shortcut — no new actions, no
 * parallel implementation. `commands.ts`'s `buildCommands` wires each entry
 * straight to the same underlying function its menu equivalent already
 * calls; this component only owns the search text, the keyboard-selected
 * row, and the open/close chrome.
 *
 * A backdrop-modal like `ShortcutCheatsheet.tsx`, not a `useMenu` dropdown
 * — it opens from a global Cmd/Ctrl+K keydown (App.tsx), not a trigger
 * button, so outside-click-to-close is the same "backdrop's own onClick,
 * inner dialog stops propagation" shape `ShortcutCheatsheet.tsx` already
 * uses, and Escape is `useEscapeKey` directly rather than the whole
 * `useMenu` shape a dropdown needs.
 */
import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import {
  buildCommands,
  CLEAR_ALL_DATA_CONFIRM_MESSAGE,
  filterCommands,
  type Command,
  type CommandsContext,
} from '../lib/commands';
import { useEscapeKey } from './useEscapeKey';

interface CommandPaletteProps extends CommandsContext {
  onClose: () => void;
}

export default function CommandPalette({ onClose, ...ctx }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  // A destructive command (only "Clear all local data" carries
  // `destructive: true`) never runs straight off a selection — see
  // commands.ts's own doc comment for why `onClearAllData` alone isn't a
  // safe substitute for AboutMenu.tsx's confirm step. Selecting one here
  // parks it in this state and swaps the whole dialog to a confirm card
  // instead, mirroring AboutMenu.tsx's own `confirming` shape.
  const [pendingCommand, setPendingCommand] = useState<Command | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEscapeKey(onClose, true);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Cheap to recompute every render (array construction over ~50 static
  // entries, no rendering work) — not worth a useMemo whose dependency
  // (the spread `ctx`) is a fresh object every render anyway.
  const commands = buildCommands(ctx);
  const filtered = filterCommands(commands, query);
  const clampedIndex = Math.min(selectedIndex, Math.max(filtered.length - 1, 0));

  function handleQueryChange(next: string) {
    setQuery(next);
    setSelectedIndex(0);
  }

  function runCommand(command: Command) {
    command.run();
    onClose();
  }

  function selectCommand(command: Command) {
    if (command.destructive) {
      setPendingCommand(command);
      return;
    }
    runCommand(command);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const command = filtered[clampedIndex];
      if (command) selectCommand(command);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink/80 pt-24" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[60vh] w-full max-w-md flex-col overflow-hidden rounded-sm border border-ink-3 bg-ink-2 shadow-lg"
      >
        {pendingCommand ? (
          <div className="px-3 py-3">
            <p className="text-xs text-text-ink">{CLEAR_ALL_DATA_CONFIRM_MESSAGE}</p>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingCommand(null)}
                className="rounded-sm px-2 py-1 text-xs text-mute hover:bg-ink-3 hover:text-text-ink"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => runCommand(pendingCommand)}
                className="rounded-sm px-2 py-1 text-xs text-pink hover:bg-ink-3"
              >
                {pendingCommand.label}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-ink-3 px-3 py-2">
              <Search size={14} className="text-mute" aria-hidden />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a command…"
                aria-label="Search commands"
                aria-controls="command-palette-list"
                aria-activedescendant={filtered[clampedIndex]?.id}
                className="w-full bg-transparent text-xs text-text-ink outline-none placeholder:text-mute"
              />
            </div>
            <ul id="command-palette-list" role="listbox" aria-label="Commands" className="overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-1.5 text-xs text-mute">No matching commands.</li>
              ) : (
                filtered.map((command, index) => (
                  <li key={command.id} role="presentation">
                    <button
                      id={command.id}
                      type="button"
                      role="option"
                      aria-selected={index === clampedIndex}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onClick={() => selectCommand(command)}
                      className={`block w-full px-3 py-1.5 text-left text-xs text-text-ink ${
                        index === clampedIndex ? 'bg-ink-3' : ''
                      }`}
                    >
                      {command.label}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
